import { useCallback, useEffect, useState } from 'react';
import { ArrowClockwise, Warning } from 'shared/icons';
import api from '../../services/api';
import { FormatDate } from '../../utils/date';
import { FormatInt, FormatPercent } from '../../utils/format';
import Button from '../ui/Button';
import { DateTimeText, Id } from '../ui/Mono';
import { SectionCard, StatusDot, TopCard } from './HealthUi';

/**
 * Aba "Notas Fiscais" do /health — só quantidades: quantos documentos fiscais o
 * runner de NF-e baixou, quantos estão guardados no S3, como foram comunicados à
 * Occto (sucesso/falha/pendente) e quantos já estão no Drive, no total e dia a
 * dia, com a coleta de cada fonte por dia. Fonte: GET /health/fiscal.
 */

const PERIOD_OPTIONS = [7, 30, 90];

// Colunas da tabela dia a dia: [cabeçalho, explicação no mouse].
const DAY_COLUMNS = [
    ['Dia', 'Data fiscal do documento (emissão, evento)'],
    ['Baixadas', 'Documentos guardados pelo runner, de todas as fontes'],
    ['No S3', 'Guardados no S3 (cópia quente)'],
    ['Occto ✓', 'Enviados à Occto, ou que ela já tinha'],
    ['Provadas', 'A Occto devolveu o documento numa consulta (GET)'],
    ['A provar', 'Enviados, esperando a prova da próxima rodada'],
    ['Occto ✗', 'A Occto recusou (tentado de novo a cada rodada)'],
    ['Pendentes', 'Ainda não enviados (inclui evento esperando a nota)'],
    ['Não vão à Occto', 'Por regra não se enviam — passe o mouse no número para ver o motivo'],
    ['Conferência', 'Baixadas − (✓ + ✗ + Pendentes + Não vão). Tem de ser ✓'],
    ['Drive', 'Conferidos no backup do Google Drive'],
    ['Lote ML', 'Arquivos no lote de notas do Mercado Livre do dia'],
    ['Bling saídas', 'Notas que SÓ o Bling tinha (o resto da listagem veio pelo lote do ML)'],
    ['Compras', 'Notas de fornecedor emitidas no dia'],
];
const REFRESH_MS = 60_000;

const DOC_TYPE_LABELS = {
    saida: 'Saídas',
    entrada_propria: 'Entradas próprias',
    entrada_terceiro: 'Entradas de fornecedor',
    cte: 'CT-e',
    evento: 'Eventos',
    inutilizacao: 'Inutilizações',
    nao_classificado: 'Não classificados',
};

const SOURCE_LABELS = { ML_BATCH: 'Lote do ML', BLING: 'Bling', DFE: 'DF-e', S3: 'Conciliação S3' };

// Por que um documento NÃO vai à Occto (motivo gravado pelo runner em occto_message).
const NOT_SENT_REASON_LABELS = {
    evento_nao_registrado: 'evento recusado pela SEFAZ',
    evento_fora_do_escopo: 'evento que não é cancelamento (ex.: carta de correção)',
    inutilizacao: 'inutilização (a Occto não recebe)',
    nao_classificado: 'arquivo que não é documento fiscal',
    antes_do_escopo_da_occto: 'emitido antes de 01/07 (escopo da Occto)',
    nfe_sem_protocolo_aceito: 'NF-e sem autorização da SEFAZ',
    nfe_sem_nfeProc: 'NF-e sem o protocolo anexado',
    cte_sem_protocolo_aceito: 'CT-e sem autorização da SEFAZ',
};

const reasonsTitle = (reasons) =>
    Object.entries(reasons || {})
        .sort((a, b) => b[1] - a[1])
        .map(([reason, count]) => `${FormatInt(count)} × ${labelFor(NOT_SENT_REASON_LABELS, reason)}`)
        .join('\n');

const SOURCE_DAY_STYLES = {
    ok: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
    vazio: 'bg-mono-100 text-mono-600 dark:bg-mono-800 dark:text-mono-300',
    parcial: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
    falha: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
    // Bling: nota nossa esperando o lote do ML do dia, ou o teto de chamadas da rodada.
    espera_ml: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300',
    espera_cota: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300',
    espera_sefaz: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300',
    // Lote do ML cortado vezes demais: o dia saiu da fila (CT-e desse dia só vinha no lote).
    desistido: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
};

const labelFor = (labels, key) => labels[key] || key;

const breakdown = (counts, labels) =>
    Object.entries(counts || {})
        .sort((a, b) => b[1] - a[1])
        .map(([key, count]) => `${labelFor(labels, key)} ${FormatInt(count)}`)
        .join(' · ');

// Pílula com a cor do status da coleta daquele dia e o número que importa naquela coluna.
const SourcePill = ({ entry, value, title }) => {
    if (!entry) {
        return <span className="text-mono-400">—</span>;
    }
    return (
        <span
            title={title}
            className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold ${SOURCE_DAY_STYLES[entry.status] || SOURCE_DAY_STYLES.vazio}`}
        >
            {entry.status === 'falha' ? 'falha' : FormatInt(value)}
        </span>
    );
};

// Saídas no Bling: o que importa é o que SÓ o Bling tinha (série 1 do ERP e sobras) —
// o resto da listagem (~2 mil/dia) é a mesma nota que o lote do ML já trouxe.
const BlingSaidasCell = ({ entry }) => (
    <SourcePill
        entry={entry}
        value={entry?.new_documents}
        title={
            entry
                ? [
                      `${FormatInt(entry.new_documents)} notas que só o Bling tinha (baixadas dele)`,
                      `${FormatInt(entry.documents)} saídas listadas no Bling neste dia — o resto já tinha vindo pelo lote do ML`,
                      `situação da varredura: ${entry.status} · ${FormatInt(entry.attempts)} passada(s)`,
                      entry.last_error ? `erro: ${entry.last_error}` : null,
                  ]
                      .filter(Boolean)
                      .join('\n')
                : undefined
        }
    />
);

// Compras = notas de FORNECEDOR emitidas no dia. A listagem de "entradas" do Bling traz
// também os retornos simbólicos NOSSOS (~2 mil/dia), que não são compra.
const ComprasCell = ({ entry, supplierInvoices }) => (
    <SourcePill
        entry={entry ?? (supplierInvoices ? { status: 'ok' } : null)}
        value={supplierInvoices}
        title={
            [
                `${FormatInt(supplierInvoices)} notas de fornecedor emitidas neste dia`,
                entry
                    ? `listagem de entradas do Bling: ${FormatInt(entry.documents)} linhas (quase todas retornos simbólicos nossos) · ${entry.status}`
                    : null,
            ]
                .filter(Boolean)
                .join('\n')
        }
    />
);

const RunnerBanner = ({ runner }) => {
    const job = runner?.job;
    if (!runner?.enabled_here) {
        return (
            <div className="flex items-start gap-3 rounded-xl border border-orange-200 bg-orange-50 px-4 py-3 text-sm text-orange-800 dark:border-orange-900/60 dark:bg-orange-950/40 dark:text-orange-200">
                <Warning size={18} className="mt-0.5 shrink-0" />
                <div>
                    <p className="font-semibold">Runner de NF-e desligado neste servidor</p>
                    <p className="text-xs">
                        Sem <span className="font-mono">ENABLE_NFE_RUNNER=true</span>, nada é baixado, guardado ou
                        enviado à Occto automaticamente. Os números abaixo são o que já está no banco.
                    </p>
                </div>
            </div>
        );
    }
    const failed = job?.status === 'FAILED';
    return (
        <div
            className={`flex flex-wrap items-center gap-x-4 gap-y-1 rounded-xl border px-4 py-3 text-sm ${
                failed
                    ? 'border-red-200 bg-red-50 text-red-800 dark:border-red-900/60 dark:bg-red-950/40 dark:text-red-200'
                    : 'border-mono-200 bg-white text-mono-700 dark:border-mono-800 dark:bg-mono-950 dark:text-mono-200'
            }`}
        >
            <span className="flex items-center gap-2 font-semibold">
                <StatusDot ok={!failed && job?.status !== 'RUNNING'} warn={job?.status === 'RUNNING'} />
                Runner ligado · modo {runner.mode}
                {job?.status ? ` · ${job.status}` : ''}
            </span>
            <span className="text-xs">
                Última execução: <DateTimeText value={job?.last_run} relative />
            </span>
            <span className="text-xs">
                Próxima: <DateTimeText value={job?.next_run_at} />
            </span>
            {failed && job?.error_log ? (
                <span className="w-full truncate text-xs" title={job.error_log}>
                    {job.error_log}
                </span>
            ) : null}
        </div>
    );
};

const FiscalHealthPanel = () => {
    const [days, setDays] = useState(30);
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [updatedAt, setUpdatedAt] = useState(null);

    const load = useCallback(async () => {
        try {
            setError(null);
            const res = await api.get(`/health/fiscal?days=${days}`);
            setData(res?.data ?? res);
            setUpdatedAt(new Date());
        } catch (err) {
            setError(err.message || 'Erro ao carregar as notas fiscais');
        } finally {
            setLoading(false);
        }
    }, [days]);

    useEffect(() => {
        load();
        const timer = setInterval(load, REFRESH_MS);
        return () => clearInterval(timer);
    }, [load]);

    if (loading && !data) {
        return (
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-5">
                {[...Array(5)].map((_, i) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <div key={i} className="h-28 animate-pulse rounded-xl border border-mono-200 bg-mono-100 dark:border-mono-800 dark:bg-mono-900" />
                ))}
            </div>
        );
    }

    if (error && !data) {
        return (
            <div className="rounded-xl border border-mono-200 bg-white p-10 text-center dark:border-mono-800 dark:bg-mono-950">
                <Warning size={40} className="mx-auto mb-3 text-red-500" />
                <p className="text-mono-500">{error}</p>
                <Button onClick={load} size="sm" className="mt-4">
                    Tentar novamente
                </Button>
            </div>
        );
    }

    const totals = data?.totals || {};
    const occto = totals.occto || {};
    const drive = totals.drive || {};
    const inbound = totals.inbound_invoices || {};
    const documents = totals.documents || 0;

    return (
        <div className="space-y-6">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                    <span className="text-xs text-mono-500">Período:</span>
                    {PERIOD_OPTIONS.map((option) => (
                        <button
                            key={option}
                            type="button"
                            onClick={() => setDays(option)}
                            aria-pressed={days === option}
                            className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                                days === option
                                    ? 'border-black bg-black text-white dark:border-white dark:bg-white dark:text-black'
                                    : 'border-mono-200 bg-white text-mono-600 hover:border-mono-300 dark:border-mono-800 dark:bg-mono-950 dark:text-mono-400'
                            }`}
                        >
                            {option} dias
                        </button>
                    ))}
                </div>
                <div className="flex items-center gap-3">
                    {updatedAt ? (
                        <span className="text-xs text-mono-500">Atualizado: {updatedAt.toLocaleTimeString('pt-BR')}</span>
                    ) : null}
                    <Button onClick={load} size="sm">
                        <ArrowClockwise size={16} className="mr-2" /> Atualizar
                    </Button>
                </div>
            </div>

            <RunnerBanner runner={data?.runner} />

            {/* Totais — o acervo inteiro que o banco conhece, não só o período */}
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-5">
                <TopCard label="Baixadas" sub={breakdown(totals.by_source, SOURCE_LABELS) || 'nenhum documento'}>
                    {FormatInt(documents)}
                </TopCard>
                <TopCard
                    label="Guardadas no S3"
                    sub={
                        data?.activity?.last_stored_at ? (
                            <>
                                última: <DateTimeText value={data.activity.last_stored_at} relative />
                            </>
                        ) : (
                            'nenhuma gravação'
                        )
                    }
                >
                    {FormatInt(totals.stored_s3)}
                    {documents > 0 ? (
                        <span className="ml-2 text-sm font-medium text-mono-500">
                            {FormatPercent((100 * (totals.stored_s3 || 0)) / documents)}
                        </span>
                    ) : null}
                </TopCard>
                <TopCard
                    label="Comunicadas à Occto"
                    sub={`${FormatInt(occto.enviado)} enviadas · ${FormatInt(occto.ja_existia)} já estavam lá`}
                >
                    <span className="flex items-center gap-2">
                        <StatusDot ok={(occto.falha || 0) === 0 && (occto.pendente || 0) === 0} warn={(occto.falha || 0) === 0} />
                        {FormatInt(occto.sucesso)}
                    </span>
                    <span className="mt-1 block text-xs font-medium">
                        <span className={occto.falha ? 'text-red-600 dark:text-red-400' : 'text-mono-500'}>
                            {FormatInt(occto.falha)} com falha
                        </span>
                        <span className="text-mono-500"> · </span>
                        <span className={occto.pendente ? 'text-orange-600 dark:text-orange-400' : 'text-mono-500'}>
                            {FormatInt(occto.pendente)} pendentes
                        </span>
                        {occto.aguardando_nota ? (
                            <>
                                <span className="text-mono-500"> · </span>
                                <span className="text-orange-600 dark:text-orange-400">
                                    {FormatInt(occto.aguardando_nota)} eventos aguardando a nota
                                </span>
                            </>
                        ) : null}
                    </span>
                    {totals.occto_exhausted ? (
                        <span className="mt-1 block text-xs font-semibold text-red-600 dark:text-red-400">
                            {FormatInt(totals.occto_exhausted)} parados: tentativas esgotadas
                        </span>
                    ) : null}
                    {/* Provado = a Occto devolveu o documento numa consulta (GET), não só "o envio respondeu ok". */}
                    <span className="mt-1 block text-xs font-medium text-green-700 dark:text-green-400">
                        {FormatInt(totals.occto_verified)} provadas na Occto
                        {occto.sucesso > 0 ? ` (${FormatPercent((100 * (totals.occto_verified || 0)) / occto.sucesso)})` : ''}
                        {totals.occto_to_prove ? (
                            <span className="text-mono-500"> · {FormatInt(totals.occto_to_prove)} a provar na próxima rodada</span>
                        ) : null}
                    </span>
                    {occto.nao_aplicavel ? (
                        <span className="mt-1 block text-xs text-mono-500" title={reasonsTitle(totals.occto_not_sent_reasons)}>
                            {FormatInt(occto.nao_aplicavel)} não vão à Occto por regra (passe o mouse para ver os motivos)
                        </span>
                    ) : null}
                </TopCard>
                <TopCard
                    label="Salvas no Drive"
                    sub={
                        drive.stage_implemented
                            ? `${FormatInt(drive.packaged)} empacotadas`
                            : 'a etapa do Drive ainda não existe no runner'
                    }
                >
                    {drive.stage_implemented ? FormatInt(drive.verified) : <span className="text-mono-400">—</span>}
                </TopCard>
                <TopCard label="Notas de entrada" sub={breakdown(inbound.by_source, {}) || 'nenhuma importada'}>
                    {FormatInt(inbound.total)}
                </TopCard>
            </div>

            <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
                <SectionCard title="Por tipo de documento">
                    <table className="w-full text-sm">
                        <tbody>
                            {Object.entries(totals.by_type || {})
                                .sort((a, b) => b[1] - a[1])
                                .map(([type, count]) => (
                                    <tr key={type} className="border-b border-mono-50 last:border-b-0 dark:border-mono-900/60">
                                        <td className="px-4 py-2 text-mono-500">{labelFor(DOC_TYPE_LABELS, type)}</td>
                                        <td className="px-4 py-2 text-right font-semibold tabular-nums text-black dark:text-white">
                                            {FormatInt(count)}
                                        </td>
                                    </tr>
                                ))}
                            {documents === 0 ? (
                                <tr>
                                    <td className="px-4 py-3 text-xs text-mono-500">Nenhum documento no banco.</td>
                                </tr>
                            ) : null}
                        </tbody>
                    </table>
                </SectionCard>

                <div className="lg:col-span-2">
                    <SectionCard
                        title="Últimas falhas na Occto"
                        badge={
                            <span className="rounded-full bg-mono-100 px-2 py-0.5 text-xs font-semibold text-mono-600 dark:bg-mono-800 dark:text-mono-300">
                                {FormatInt(occto.falha)}
                            </span>
                        }
                    >
                        {(data?.occto_failures || []).length === 0 ? (
                            <p className="px-4 py-3 text-xs text-mono-500">Nenhuma falha registrada.</p>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full min-w-[560px] text-xs">
                                    <tbody>
                                        {data.occto_failures.map((failure) => (
                                            <tr key={failure.identity} className="border-b border-mono-50 last:border-b-0 dark:border-mono-900/60">
                                                <td className="px-4 py-2">
                                                    <Id value={failure.identity} />
                                                </td>
                                                <td className="px-4 py-2 text-mono-500">{labelFor(DOC_TYPE_LABELS, failure.doc_type)}</td>
                                                <td className="px-4 py-2 tabular-nums text-mono-500">{FormatDate(failure.document_date)}</td>
                                                <td className="px-4 py-2 tabular-nums text-mono-500">{FormatInt(failure.attempts)}×</td>
                                                <td className="max-w-[280px] truncate px-4 py-2 text-red-500" title={failure.message || ''}>
                                                    {failure.message || '—'}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </SectionCard>
                </div>
            </div>

            <SectionCard
                title="Dia a dia"
                badge={<span className="text-xs text-mono-500">pela data fiscal do documento</span>}
            >
                <div className="overflow-x-auto">
                    <table className="w-full min-w-[1080px] text-xs">
                        <thead>
                            <tr className="border-b border-mono-100 dark:border-mono-900">
                                {DAY_COLUMNS.map(([header, hint]) => (
                                    <th
                                        key={header}
                                        title={hint}
                                        className="px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-wide text-mono-400"
                                    >
                                        {header}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {(data?.days || []).map((day) => (
                                <tr
                                    key={day.day}
                                    className="border-b border-mono-50 last:border-b-0 hover:bg-mono-50 dark:border-mono-900/60 dark:hover:bg-mono-900/40"
                                >
                                    <td className="px-3 py-2 tabular-nums text-black dark:text-white">{FormatDate(day.day)}</td>
                                    <td className="px-3 py-2 tabular-nums font-semibold text-black dark:text-white">
                                        {FormatInt(day.documents)}
                                    </td>
                                    <td className="px-3 py-2 tabular-nums text-mono-600 dark:text-mono-300">{FormatInt(day.stored_s3)}</td>
                                    <td className="px-3 py-2 tabular-nums text-green-700 dark:text-green-400">{FormatInt(day.occto.sucesso)}</td>
                                    <td className="px-3 py-2 tabular-nums text-green-700 dark:text-green-400">
                                        {FormatInt(day.occto_verified)}
                                    </td>
                                    <td className={`px-3 py-2 tabular-nums ${day.occto_to_prove ? 'text-blue-600 dark:text-blue-400' : 'text-mono-400'}`}>
                                        {FormatInt(day.occto_to_prove)}
                                    </td>
                                    <td className={`px-3 py-2 tabular-nums ${day.occto.falha ? 'text-red-600 dark:text-red-400' : 'text-mono-400'}`}>
                                        {FormatInt(day.occto.falha)}
                                    </td>
                                    <td
                                        className={`px-3 py-2 tabular-nums ${day.occto.pendente || day.occto.aguardando_nota ? 'text-orange-600 dark:text-orange-400' : 'text-mono-400'}`}
                                        title={day.occto.aguardando_nota ? `${day.occto.aguardando_nota} eventos aguardando a nota` : undefined}
                                    >
                                        {FormatInt((day.occto.pendente || 0) + (day.occto.aguardando_nota || 0))}
                                    </td>
                                    <td
                                        className={`px-3 py-2 tabular-nums ${day.occto.nao_aplicavel ? 'cursor-help text-mono-600 underline decoration-dotted dark:text-mono-300' : 'text-mono-400'}`}
                                        title={reasonsTitle(day.occto_not_sent_reasons) || undefined}
                                    >
                                        {FormatInt(day.occto.nao_aplicavel)}
                                    </td>
                                    <td
                                        className={`px-3 py-2 tabular-nums ${day.unexplained ? 'font-semibold text-red-600 dark:text-red-400' : 'text-mono-300 dark:text-mono-600'}`}
                                        title={day.unexplained ? 'Baixadas que nenhuma coluna explica: avisar o desenvolvimento' : 'Baixadas = Occto ✓ + ✗ + Pendentes + Não vão à Occto'}
                                    >
                                        {day.unexplained ? FormatInt(day.unexplained) : '✓'}
                                    </td>
                                    <td className="px-3 py-2 tabular-nums text-mono-400">
                                        {drive.stage_implemented ? FormatInt(day.drive_verified) : '—'}
                                    </td>
                                    <td className="px-3 py-2">
                                        <SourcePill
                                            entry={day.sources?.ML_BATCH}
                                            value={day.sources?.ML_BATCH?.documents}
                                            title={
                                                day.sources?.ML_BATCH
                                                    ? [
                                                          `${FormatInt(day.sources.ML_BATCH.documents)} arquivos no lote do ML (${FormatInt(day.sources.ML_BATCH.new_documents)} novos)`,
                                                          `situação: ${day.sources.ML_BATCH.status} · ${FormatInt(day.sources.ML_BATCH.attempts)} tentativa(s)`,
                                                          day.sources.ML_BATCH.last_error ? `erro: ${day.sources.ML_BATCH.last_error}` : null,
                                                      ]
                                                          .filter(Boolean)
                                                          .join('\n')
                                                    : undefined
                                            }
                                        />
                                    </td>
                                    <td className="px-3 py-2">
                                        <BlingSaidasCell entry={day.sources?.BLING_SAIDAS} />
                                    </td>
                                    <td className="px-3 py-2">
                                        <ComprasCell entry={day.sources?.BLING_ENTRADAS} supplierInvoices={day.supplier_invoices} />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <p className="border-t border-mono-100 px-4 py-3 text-[11px] leading-relaxed text-mono-500 dark:border-mono-900">
                    Cada linha fecha a conta: <strong>Baixadas = Occto ✓ + Occto ✗ + Pendentes + Não vão à Occto</strong> (a coluna
                    Conferência mostra ✓ quando fecha). <strong>Provadas</strong> são as que a Occto devolveu numa consulta; <strong>A
                    provar</strong> foram enviadas e esperam a prova da próxima rodada. <strong>Não vão à Occto</strong> são documentos
                    que por regra não se enviam (passe o mouse para ver o motivo). <strong>Bling saídas</strong> conta só as notas que
                    o Bling tinha e o lote do ML não; <strong>Compras</strong> são as notas de fornecedor do dia.
                </p>
            </SectionCard>
        </div>
    );
};

export default FiscalHealthPanel;
